import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import VirtualizedCompanyDropdown, {
  CompanyOption,
} from "./VirtualizedCompanyDropdown";

export interface ProjectOrganizationFilterData {
  participantCompanies: CompanyOption[];
  maxTiers: number;
}

interface ProjectOrganizationFilterProps {
  filter: ProjectOrganizationFilterData;
  onChange: (updatedFilter: ProjectOrganizationFilterData) => void;
}

// Tier options for project participation depth
const TIER_OPTIONS = [
  { value: 1, label: "Tier 1 (Direct)" },
  { value: 2, label: "Tier 2" },
  { value: 3, label: "Tier 3" },
  { value: 4, label: "Tier 4" },
  { value: 5, label: "Tier 5" },
];

const ProjectOrganizationFilter: React.FC<ProjectOrganizationFilterProps> = ({
  filter,
  onChange,
}) => {
  const handleCompaniesChange = (companies: CompanyOption[]) => {
    onChange({
      ...filter,
      participantCompanies: companies,
    });
  };

  const handleTierChange = (event: SelectChangeEvent<number>) => {
    onChange({
      ...filter,
      maxTiers: event.target.value as number,
    });
  };

  return (
    <Box>
      <Stack spacing={2}>
        <Typography variant="subtitle2" fontWeight="medium">
          Project Participants
        </Typography>

        <VirtualizedCompanyDropdown
          label="Participant Companies"
          value={filter.participantCompanies}
          onChange={handleCompaniesChange}
          placeholder="Search for participant companies..."
          multiple={true}
        />

        {/* Tier selection */}
        <FormControl size="small" fullWidth>
          <InputLabel>Participation Depth</InputLabel>
          <Select
            value={filter.maxTiers}
            onChange={handleTierChange}
            label="Participation Depth"
          >
            {TIER_OPTIONS.map((tier) => (
              <MenuItem key={tier.value} value={tier.value}>
                {tier.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Summary display */}
        {filter.participantCompanies.length > 0 && (
          <Box
            sx={{
              backgroundColor: "action.hover",
              borderRadius: 1,
              p: 1.5,
              mt: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: "medium" }}
            >
              Filter Summary:
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Find projects with participation by{" "}
              <strong>
                {filter.participantCompanies
                  .map((company) => company.name)
                  .join(", ")}
              </strong>{" "}
              (up to{" "}
              {filter.maxTiers === 1
                ? "direct participation"
                : `tier ${filter.maxTiers}`}
              )
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default ProjectOrganizationFilter;
